/**
 * Sim goal readouts — a FIELD READ of `summary.goals` (contract §4.3), nothing more.
 *
 * Replaces `goal-sim-eta.ts` (#1957/B4), which derived the simulated ETA in the
 * browser: `deriveSimEta` re-ran the shared `deriveGoalEta` core over the
 * ~52-week simulated series while the baseline came from the server's uncapped
 * 520-week goal ETA, and `classifySimEta` then subtracted the two. That was
 * inventory cluster D3 (the backend/frontend ETA mirror) and D4's sim member
 * (the weeks-sooner/later subtraction) in one file.
 *
 * v2 computes BOTH sides over the goal frame — 520 weeks from
 * `window.currentWeekStart`, plan-clamp bypassed (§4.3) — and ships
 * `deltaWeeks` exact. So the window asymmetry the old five-case grammar existed
 * to narrate is gone: there is no "the sim only saw 52 weeks" state left to
 * describe, and `reach` is the honest replacement.
 *
 * Nothing here computes a displayed number. It reshapes per-account
 * `GoalImpact` entries into the flat, goal-keyed list every sim surface already
 * consumes (canvas flags, ledger lines, popover, announcement).
 */

import type { AccountSimResult, GoalImpact } from 'shared/types/simulate-v2';

/**
 * Which sides of the simulation reach the goal, within the 520-week goal frame.
 *
 * Carried verbatim from the contract for savings/payoff goals. Buffer goals have
 * no week grammar at all (§4.3) and are marked `n/a` — their readout speaks in
 * shortfall dollars instead.
 */
export type SimGoalReach = 'both' | 'baseline-only' | 'simulated-only' | 'neither' | 'n/a';

/**
 * One goal's simulated-vs-baseline reading, flattened out of the per-account
 * results. Every field is copied off the response — none is derived here.
 */
export interface SimGoalReadout {
	goalId: string;
	accountId: string;
	name: string;
	kind: 'savings' | 'payoff' | 'buffer';
	/** `achieved` goals carry no ETA at all (§4.3) — check this BEFORE `kind`. */
	status: 'active' | 'achieved';
	/** Goal-frame week the SIMULATED side reaches the target; null = not within 520 weeks. */
	simEtaWeek: number | null;
	simEtaDate: string | null;
	/** The same question on the untouched trajectory — the server's own baseline. */
	baselineEtaWeek: number | null;
	baselineEtaDate: string | null;
	/** `baseline.etaWeek − simulated.etaWeek` (§5.3 family 2: positive = sooner). */
	deltaWeeks: number | null;
	reach: SimGoalReach;
	/**
	 * Buffer goals only (§4.3): `simulated.shortfallAmount −
	 * baseline.shortfallAmount` — negative = the simulation closes the gap.
	 * null for every other kind, and for a buffer goal due beyond the frame.
	 */
	shortfallDelta: number | null;
}

/**
 * The simulated ETA lands on the very week the baseline already did.
 *
 * Named because two surfaces need the same answer for opposite reasons: the
 * canvas suppresses the baseline flag (drawing both would be a ghost pair), and
 * the popover suppresses the date PAIR (there is no second date to show).
 */
export function isZeroDelta(r: SimGoalReadout): boolean {
	return r.reach === 'both' && r.deltaWeeks === 0;
}

/**
 * The simulation pushed a goal the baseline reaches out of the 520-week frame
 * entirely — the gutter edge flag's case. Distinct from `neither`, which was
 * never reachable either way and says nothing new.
 */
export function isPushedBeyond(r: SimGoalReadout): boolean {
	return r.reach === 'baseline-only';
}

function fromImpact(accountId: string, impact: GoalImpact): SimGoalReadout {
	const base = {
		goalId: impact.goalId,
		accountId,
		name: impact.name,
		kind: impact.kind
	};

	if (impact.status === 'achieved') {
		return {
			...base,
			status: 'achieved',
			simEtaWeek: null,
			simEtaDate: null,
			baselineEtaWeek: null,
			baselineEtaDate: null,
			deltaWeeks: null,
			reach: 'n/a',
			shortfallDelta: null
		};
	}

	if (impact.kind === 'buffer') {
		return {
			...base,
			status: 'active',
			simEtaWeek: null,
			simEtaDate: null,
			baselineEtaWeek: null,
			baselineEtaDate: null,
			deltaWeeks: null,
			reach: 'n/a',
			shortfallDelta: impact.shortfallDelta
		};
	}

	return {
		...base,
		status: 'active',
		simEtaWeek: impact.simulated?.etaWeek ?? null,
		simEtaDate: impact.simulated?.etaDate ?? null,
		baselineEtaWeek: impact.baseline?.etaWeek ?? null,
		baselineEtaDate: impact.baseline?.etaDate ?? null,
		deltaWeeks: impact.deltaWeeks,
		reach: impact.reach,
		shortfallDelta: null
	};
}

/**
 * Flatten every requested account's `summary.goals` into one goal-keyed list.
 *
 * The server already filtered archived and soft-deleted goals (§4.3), and only
 * projects the accounts the request named — so no client-side scoping is applied
 * here either. Order follows the response: request order across accounts, then
 * the server's own goal order within each.
 */
export function buildSimGoalReadouts(results: readonly AccountSimResult[]): SimGoalReadout[] {
	const readouts: SimGoalReadout[] = [];
	for (const result of results) {
		for (const impact of result.summary.goals) {
			readouts.push(fromImpact(result.account.id, impact));
		}
	}
	return readouts;
}
